// @ts-check
// Diálogos das telas dos bots (contrato de bots, seção 22): nome do bot (criar com a finalidade, renomear), outra
// pessoa salvou antes (recarregar ou criar uma cópia com as minhas alterações), sair sem salvar, excluir um bloco com
// ramos (qual ramo fica), confirmar a troca de opções que leva ramos com blocos, desativar e excluir o bot.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { formatTime } from '../../lib/format.js';
import { MAXIMUM_BOT_NAME_LENGTH } from '../../../../shared/bots/bot-limits.js';
import { BOT_PURPOSES } from '../../../../shared/bots/bot-definition.js';
import { FlowDialog } from '../flows/flow-dialog.js';
import { FlowRadioOption } from '../flows/flow-lifecycle-dialogs.js';
import { FlowField } from '../flows/flow-ui.js';
import { useFieldId } from './bot-form-fields.js';
import { t } from '../../i18n/index.js';

/**
 * Nome do bot (e a finalidade, ao criar em branco).
 * @param {{ mode: 'create' | 'edit', initialName?: string, initialPurpose?: string, existingNames?: string[], onClose: () => void,
 *   onSubmit: (value: { name: string, purpose: string }) => Promise<boolean> | boolean, title?: string, submitLabel?: string }} props
 */
export function BotNameDialog({ mode, initialName = '', initialPurpose = 'ANY', existingNames = [], onClose, onSubmit, title, submitLabel }) {
  const [name, setName] = useState(initialName);
  const [purpose, setPurpose] = useState(initialPurpose);
  const [isBusy, setIsBusy] = useState(false);
  const id = useFieldId('bot-name');
  const trimmed = name.trim();
  const isDuplicate = existingNames.some((existing) => existing.trim().toLowerCase() === trimmed.toLowerCase() && existing.trim() !== initialName.trim());
  const error = trimmed.length > MAXIMUM_BOT_NAME_LENGTH ? t('bots.nameDialog.tooLong', { maximum: MAXIMUM_BOT_NAME_LENGTH }) : null;
  const submit = async () => {
    if (!trimmed || error || isBusy) return;
    setIsBusy(true);
    const done = await onSubmit({ name: trimmed, purpose });
    if (!done) setIsBusy(false);
  };
  return html`<${FlowDialog} title=${title ?? (mode === 'create' ? t('bots.nameDialog.createTitle') : t('bots.nameDialog.editTitle'))} size="small" onClose=${onClose}
    testId="bot-name-dialog" onSubmit=${submit}
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${submitLabel ?? (mode === 'create' ? t('bots.nameDialog.create') : t('bots.nameDialog.save'))} accent="blue" size="small" type="submit"
        disabled=${!trimmed || Boolean(error)} isLoading=${isBusy} onClick=${submit} />`}>
    <${FlowField} label=${t('bots.nameDialog.name')} htmlFor=${id} error=${error} hint=${isDuplicate ? t('bots.nameDialog.duplicate') : null}>
      <input id=${id} class="text-input" value=${name} maxLength=${MAXIMUM_BOT_NAME_LENGTH + 10} data-autofocus placeholder=${t('bots.nameDialog.placeholder')}
        onInput=${(/** @type {InputEvent} */ event) => setName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} data-testid="bot-name-input" />
    <//>
    ${mode === 'create'
      ? html`<div class="flow-radio-group" role="radiogroup" aria-label=${t('bots.settings.purpose')}>
          ${BOT_PURPOSES.map((value) => html`<${FlowRadioOption} key=${value} name="bot-purpose" value=${value} checked=${purpose === value}
            title=${t(`bots.purposes.${value}`)} description=${t(`bots.purposeHints.${value}`)} onSelect=${() => setPurpose(value)} />`)}
        </div>`
      : null}
  <//>`;
}

/**
 * "Outra pessoa salvou este bot".
 * @param {{ savedBy: { name?: string | null } | null, savedAt: string | null, isBusy: boolean, onReload: () => void, onCopy: () => void, onClose: () => void }} props
 */
export function BotConflictModal({ savedBy, savedAt, isBusy, onReload, onCopy, onClose }) {
  return html`<${FlowDialog} title=${t('bots.conflict.title')} size="small" onClose=${onClose} testId="bot-conflict" role="alertdialog"
    footer=${html`<${Button} title=${t('bots.conflict.reload')} variant="secondary" size="small" onClick=${onReload} />
      <${Button} title=${t('bots.conflict.copy')} accent="blue" size="small" isLoading=${isBusy} onClick=${onCopy} />`}>
    <p class="flow-dialog-text">${savedBy?.name && savedAt ? t('bots.conflict.savedBy', { name: savedBy.name, time: formatTime(savedAt) }) : t('bots.conflict.savedBySomeone')}</p>
    <p class="flow-dialog-text">${t('bots.conflict.text')}</p>
  <//>`;
}

/** "Sair sem salvar?". @param {{ canSave: boolean, isBusy: boolean, onSaveAndLeave: () => void, onLeave: () => void, onClose: () => void }} props */
export function BotLeaveModal({ canSave, isBusy, onSaveAndLeave, onLeave, onClose }) {
  return html`<${FlowDialog} title=${t('bots.leave.title')} size="small" onClose=${onClose} testId="bot-leave" role="alertdialog"
    footer=${html`<${Button} title=${t('bots.leave.keepEditing')} variant="tertiary" size="small" onClick=${onClose} />
      <${Button} title=${t('bots.leave.leave')} variant="secondary" accent="danger" size="small" onClick=${onLeave} />
      ${canSave ? html`<${Button} title=${t('bots.leave.saveAndLeave')} accent="blue" size="small" isLoading=${isBusy} onClick=${onSaveAndLeave} />` : null}`}>
    <p class="flow-dialog-text">${t('bots.leave.text')}</p>
  <//>`;
}

/**
 * "Excluir bloco com ramos": todos os ramos, ou manter um deles no lugar do bloco.
 * @param {{ branches: Array<{ key: string, label: string, count: number }>, total: number, onConfirm: (keepBranch: string | null) => void, onClose: () => void }} props
 */
export function BotDeleteSplitModal({ branches, total, onConfirm, onClose }) {
  const [choice, setChoice] = useState('ALL');
  const withNodes = branches.filter((branch) => branch.count > 0);
  return html`<${FlowDialog} title=${t('bots.deleteSplit.title')} size="small" onClose=${onClose} testId="bot-delete-split" role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('bots.deleteSplit.confirm')} accent="danger" size="small" onClick=${() => onConfirm(choice === 'ALL' ? null : choice)} />`}>
    <div class="flow-radio-group" role="radiogroup" aria-label=${t('bots.deleteSplit.title')}>
      <${FlowRadioOption} name="bot-delete-split" value="ALL" checked=${choice === 'ALL'} title=${t('bots.deleteSplit.all', { count: total + 1 })} onSelect=${() => setChoice('ALL')} />
      ${withNodes.map((branch) => html`<${FlowRadioOption} key=${branch.key} name="bot-delete-split" value=${branch.key} checked=${choice === branch.key}
        title=${t('bots.deleteSplit.keep', { branch: branch.label })} description=${t('bots.deleteSplit.keepHint', { count: branch.count })} onSelect=${() => setChoice(branch.key)} />`)}
    </div>
  <//>`;
}

/**
 * Confirmar uma mudança que tira ramos com blocos (excluir a opção, trocar botões por opções do cadastro).
 * @param {{ branches: Array<{ label: string, count: number }>, onConfirm: () => void, onClose: () => void }} props
 */
export function BotDropBranchesConfirm({ branches, onConfirm, onClose }) {
  const total = branches.reduce((sum, branch) => sum + branch.count, 0);
  return html`<${FlowDialog} title=${t('bots.dropBranches.title')} size="small" onClose=${onClose} testId="bot-drop-branches" role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t('bots.dropBranches.confirm')} accent="danger" size="small" onClick=${onConfirm} data-testid="bot-drop-branches-confirm" />`}>
    <p class="flow-dialog-text">${t('bots.dropBranches.text', { count: total })}</p>
    <ul class="bot-dialog-list">${branches.map((branch) => html`<li key=${branch.label}>${t('bots.dropBranches.item', { branch: branch.label, count: branch.count })}</li>`)}</ul>
  <//>`;
}

/**
 * Desativar ou excluir o bot (as execuções abertas param sem mensagem).
 * @param {{ kind: 'deactivate' | 'delete', name: string, isBusy: boolean, onConfirm: () => void, onClose: () => void }} props
 */
export function BotLifecycleConfirm({ kind, name, isBusy, onConfirm, onClose }) {
  return html`<${FlowDialog} title=${t(`bots.lifecycle.${kind}.title`, { name })} size="small" onClose=${onClose} testId=${`bot-${kind}-dialog`} role="alertdialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${t(`bots.lifecycle.${kind}.confirm`)} accent="danger" size="small" isLoading=${isBusy} onClick=${onConfirm} data-testid=${`bot-${kind}-confirm`} />`}>
    <p class="flow-dialog-text">${t(`bots.lifecycle.${kind}.text`)}</p>
  <//>`;
}
